/**
 * Preloader is element which shows loading status as percentages to user.
 *
 * @example
 *
 * // Get preloader element and attach event listeners if needed
 * const element = document.querySelector('[data-preloader]');
 * element.addEventListener('preloader.progress', (e) => console.log(e.detail.progress));
 *
 * // Create preloader instance
 * const preloader = new Preloader(element);
 *
 * // Start animation
 * preloader.start();
 *
 * // Complete animation
 * setTimeout(() => { preloader.complete(); }, 1000);
 */
class Preloader {

  /**
   * @param {HTMLElement} element Preloader element.
   * @param {Object} options Preloader options.
   * @param {Number} [options.duration=300] Duration of one animation interval in milliseconds.
   * @param {Number} [options.completionDuration=300] Duration of last animation interval in milliseconds.
   * @param {Number} [options.progressChangePerInterval] Progress change percentage per active interval.
   * @param {String} [options.activeClass='preloader-active'] CSS class for active state.
   * @param {String} [options.activeClass='preloader-completed'] CSS class for completed state.
   */
  constructor(element, options) {
    const defaults = {
      duration: 300,
      progressChangePerInterval: 0.5,
      completionDuration: 300,
      overlayClass: 'preloader-overlay',
      activeClass: 'preloader-active',
      completedClass: 'preloader-completed'
    };
    this.element = element;
    options = Object.assign(defaults, options || {});

    for (let key in options) {
      this[key] = options[key];
    }

    this.reinit();
  }

  /**
   * Start preloader animation.
   *
   * @param {Number} progress Initial progress from 0 to 1. Defaults to 0.
   */
  start(progress) {
    this.progress = progress || 0;
    this._started = Date.now();
    this._intervalStarted = this._started;
    this._intervalProgress = this.progress;
    this._completed = null;
    this.element.classList.add(this.activeClass);
    this._triggerEvent('preloader.started');
    this.update();
  }

  /**
   * Completes preload animation.
   */
  complete() {
    this._completed = Date.now();
    this._intervalProgress = this.progress;
    this._intervalStarted = this._completed;
    this._triggerEvent('preloader.stopped');
  }

  /**
   * Updates preloader progress.
   */
  update() {
    const duration = this._completed ? this.completionDuration : this.duration;
    const changeMultiplier = this._completed ? 1 : this.progressChangePerInterval;

    const progressLeftOnInterval = (1 - this.progress) * changeMultiplier;
    const intervalProgress = (Date.now() - this._intervalStarted) / duration;
    const intervalChange = progressLeftOnInterval * intervalProgress;
    let progress = intervalChange + this._intervalProgress;
    if (changeMultiplier === 1) {
      progress = Math.ceil(progress * 100) / 100;
    }
    progress = progress >= 1 ? 1 : progress;

    if (intervalProgress >= 1) {
      this._intervalStarted = Date.now();
      this._intervalProgress = progress;
    }

    // Update visible progress
    const visibleProgress = Math.floor(progress * 100);
    this._progressBarElement.style.width = visibleProgress + '%';
    this._textElement.innerText = visibleProgress + '%';

    this.progress = progress;
    this._triggerEvent('preloader.progress', { progress: progress });
    if (progress < 1) {
      window.requestAnimationFrame(this.update.bind(this));
    }
    else {
      this._complete();
    }
  }

  _complete() {
    this.element.classList.add(this.completedClass);
    this._triggerEvent('preloader.beforeCompletion');
    setTimeout(() => {
      this.element.classList.remove(this.completedClass);
      this.element.classList.remove(this.activeClass);
      this.element.classList.remove(this.overlayClass);
      this._triggerEvent('preloader.completed');
    }, this.completionDuration);
  }

  _triggerEvent(name, data) {
    this.element.dispatchEvent(new CustomEvent(name, { detail: data }));
  }

  /**
   * Reinitializes dom instances and event handlers.
   */
  reinit() {
    this._progressBarElement = this.element.querySelector('[data-preloader-progress]');
    this._textElement = this.element.querySelector('[data-preloader-text]');
  }

}

export default Preloader;
